import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { YenUnitToggle, YenUnit } from '@/components/ui';
import { formatMoney } from '@/lib/currency';
import { reportsApi } from '@/lib/reportsApi';

function fmtMoney(
  cents: number,
  currency = 'JPY',
  locale = 'en-US',
  unit: YenUnit = '1',
): string {
  // amount_cents is stored as the smallest currency unit. JPY has no
  // sub-unit, so we render the raw value as-is. For currencies with
  // sub-units we divide by 100.
  if (!cents) return currency === 'JPY' ? '¥0' : '0';
  const v = currency === 'JPY' ? cents : cents / 100;
  return formatMoney(v, { currency, locale, unit });
}

function fmtInt(n: number): string {
  return n.toLocaleString();
}

export function ReportsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  // Stage totals + bucket totals format at the selected unit. Hidden
  // for non-JPY workspaces by the toggle itself.
  const [amountUnit, setAmountUnit] = useState<YenUnit>('1');

  const stageQ = useQuery({
    queryKey: ['reports-pipeline-stage', wsId],
    queryFn: () => reportsApi.pipelineByStage(wsId),
    enabled: !!wsId,
  });
  const ownerQ = useQuery({
    queryKey: ['reports-pipeline-owner', wsId],
    queryFn: () => reportsApi.pipelineByOwner(wsId),
    enabled: !!wsId,
  });
  const convQ = useQuery({
    queryKey: ['reports-signal-conversion', wsId],
    queryFn: () => reportsApi.signalConversion(wsId, 30),
    enabled: !!wsId,
  });
  const seqQ = useQuery({
    queryKey: ['reports-sequence-performance', wsId],
    queryFn: () => reportsApi.sequencePerformance(wsId),
    enabled: !!wsId,
  });

  const stageRows = stageQ.data?.items ?? [];
  const ownerRows = ownerQ.data?.items ?? [];
  const convRows = convQ.data?.items ?? [];
  const seqRows = seqQ.data?.items ?? [];

  const totalOpenCount = stageRows.reduce((acc, r) => acc + r.count, 0);
  const totalOpenAmount = stageRows.reduce((acc, r) => acc + r.total_amount_cents, 0);
  const signals30d = convRows.reduce((acc, r) => acc + r.signal_count, 0);
  const sequencesActive = seqRows.filter((r) => r.enrolled > 0).length;

  return (
    <AppShell titleKey="reports.title">
      <p className="text-sm text-ink-600 max-w-2xl mb-6">{t('reports.intro')}</p>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatTile
          label={t('reports.stat_open_pipeline')}
          value={fmtInt(totalOpenCount)}
          unit={t('reports.stat_open_pipeline_unit')}
        />
        <StatTile
          label={t('reports.stat_pipeline_value')}
          value={fmtMoney(totalOpenAmount, 'JPY', i18n.language, amountUnit)}
        />
        <StatTile
          label={t('reports.stat_signals_30d')}
          value={fmtInt(signals30d)}
        />
        <StatTile
          label={t('reports.stat_sequences_active')}
          value={fmtInt(sequencesActive)}
        />
      </div>

      {/* JPY-only display-unit toggle for stage/bucket totals. */}
      <div className="mb-3 flex justify-end">
        <YenUnitToggle
          value={amountUnit}
          onChange={setAmountUnit}
          currency="JPY"
        />
      </div>

      {/* Section: pipeline by stage */}
      <ReportSection title={t('reports.section_pipeline_by_stage')} loading={stageQ.isLoading}>
        {stageRows.length === 0 ? (
          <EmptyRow text={t('reports.empty')} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-3 font-medium">{t('reports.col_stage')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_count')}</th>
                <th className="py-2 font-medium text-right">{t('reports.col_total')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {stageRows.map((r) => (
                <tr key={r.stage_key}>
                  <td className="py-2 pr-3 text-ink-900">{r.stage_label}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.count)}</td>
                  <td className="py-2 num text-right">
                    {fmtMoney(r.total_amount_cents, 'JPY', i18n.language, amountUnit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportSection>

      {/* Section: pipeline by owner */}
      <ReportSection title={t('reports.section_pipeline_by_owner')} loading={ownerQ.isLoading}>
        {ownerRows.length === 0 ? (
          <EmptyRow text={t('reports.empty')} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-3 font-medium">{t('reports.col_owner')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_open')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_total')}</th>
                <th className="py-2 font-medium text-right">{t('reports.col_won')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {ownerRows.map((r, idx) => (
                <tr key={r.owner_user_id ?? `unassigned-${idx}`}>
                  <td className="py-2 pr-3 text-ink-900">
                    {r.owner_name || '—'}
                  </td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.open_count)}</td>
                  <td className="py-2 pr-3 num text-right">
                    {fmtMoney(r.total_amount_cents, 'JPY', i18n.language, amountUnit)}
                  </td>
                  <td className="py-2 num text-right">
                    {fmtInt(r.won_count)} ·{' '}
                    {fmtMoney(r.won_amount_cents, 'JPY', i18n.language, amountUnit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportSection>

      {/* Section: signal conversion */}
      <ReportSection title={t('reports.section_signal_conversion')} loading={convQ.isLoading}>
        {convRows.length === 0 ? (
          <EmptyRow text={t('reports.empty')} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-3 font-medium">{t('reports.col_kind')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_signals')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_acted')}</th>
                <th className="py-2 pr-3 font-medium text-right">
                  {t('reports.col_sequences_started')}
                </th>
                <th className="py-2 font-medium text-right">{t('reports.col_deals_created')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {convRows.map((r) => (
                <tr key={r.kind}>
                  <td className="py-2 pr-3 text-ink-900">{r.kind}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.signal_count)}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.contacts_acted_on)}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.sequences_started)}</td>
                  <td className="py-2 num text-right">{fmtInt(r.deals_created)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportSection>

      {/* Section: sequence performance */}
      <ReportSection title={t('reports.section_sequence_performance')} loading={seqQ.isLoading}>
        {seqRows.length === 0 ? (
          <EmptyRow text={t('reports.empty')} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-3 font-medium">{t('reports.col_sequence')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_enrolled')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_replied')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('reports.col_bounced')}</th>
                <th className="py-2 font-medium text-right">{t('reports.col_finished')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {seqRows.map((r) => (
                <tr key={r.sequence_id}>
                  <td className="py-2 pr-3 text-ink-900">{r.sequence_name}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.enrolled)}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.replied)}</td>
                  <td className="py-2 pr-3 num text-right">{fmtInt(r.bounced)}</td>
                  <td className="py-2 num text-right">{fmtInt(r.finished)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportSection>
    </AppShell>
  );
}

function StatTile({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: string;
}) {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-1 text-2xl text-ink-900 num">
        {value}
        {unit && <span className="ml-1 text-sm text-ink-500">{unit}</span>}
      </div>
    </div>
  );
}

function ReportSection({
  title,
  loading,
  children,
}: {
  title: string;
  loading: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="mb-8">
      <h2 className="serif-jp display text-base text-ink-900 mb-2">{title}</h2>
      <div className="rounded-xl border border-ink-200 bg-white p-4">
        {loading ? (
          <p className="text-sm text-ink-500">{t('common.loading')}</p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <p className="text-sm text-ink-500 py-4 text-center">{text}</p>;
}
